import { h } from '../dom';
import type { ScreenCtx } from './context';
import { fixtureCard, letterBadge, screenHeader, type ScreenInstance } from './common';
import { CAREER_TIERS, PROMOTION_RATING, letterForRating, nextCareerMatch, tierAverage } from '../../meta/career';
import { UNLOCKS, unlockDef } from '../../meta/unlocks';
import { formatCompact } from '../format';
import { matchBrief } from './daily';

export function careerScreen(ctx: ScreenCtx): ScreenInstance {
  const save = ctx.store.get();
  const c = save.career;
  const plan = nextCareerMatch(c);
  const tier = plan.tier;
  const cfg = { ...ctx.deps.generateMatchConfig(plan.seed), competition: plan.competition };

  const ladder = h('ol', { class: 'sd-ladder' }, CAREER_TIERS.map((t, i) => {
    const done = c.completedTiers.includes(t.id);
    const cur = i === c.tierIndex && !c.champion;
    const state = done ? 'is-done' : cur ? 'is-current' : i > c.tierIndex ? 'is-locked' : '';
    return h('li', { class: `sd-ladder__tier ${state}`, style: `--i:${i}` }, [
      h('span', { class: 'sd-ladder__node' }, [h('span', { text: done ? '✓' : String(i + 1) })]),
      h('span', { class: 'sd-ladder__name', text: t.name.toUpperCase() }),
      h('span', { class: 'sd-ladder__aud', text: `${formatCompact(t.baseAudience)} VIEWERS` }),
      h('span', { class: 'sd-ladder__matches', text: `${t.matches} MATCH${t.matches > 1 ? 'ES' : ''}` }),
    ]);
  }));

  const avg = tierAverage(c.results);
  const pips = Array.from({ length: tier.matches }, (_, i) => {
    const r = c.results[i];
    return h('div', { class: `sd-cpip ${r ? 'is-played' : i === c.matchIndex ? 'is-next' : ''}` }, [
      r ? letterBadge(r.letter, 'sd-letter--sm') : h('span', { class: 'sd-cpip__n', text: String(i + 1) }),
      h('span', { class: 'sd-cpip__label', text: r ? String(Math.round(r.rating)) : i === c.matchIndex ? 'NEXT' : '' }),
    ]);
  });

  const avgBar = h('div', { class: 'sd-avgbar' }, [
    h('span', { class: 'sd-avgbar__fill', style: `--w:${avg / 100}` }),
    h('span', { class: 'sd-avgbar__mark', style: `--x:${PROMOTION_RATING / 100}` }, [h('span', { text: `B · ${PROMOTION_RATING}` })]),
  ]);

  const rewards = tier.rewards.map((id) => unlockDef(id)).filter(Boolean).map((u) =>
    h('div', { class: `sd-reward sd-reward--${u!.kind}` }, [h('span', { class: 'sd-reward__icon', text: u!.kind === 'camera' ? '◉' : '▤' }), h('div', {}, [h('b', { text: u!.name.toUpperCase() }), h('span', { text: u!.description })])]));

  const vault = h('div', { class: 'sd-vault' }, UNLOCKS.map((u) => {
    const got = save.unlocks.includes(u.id);
    return h('div', { class: `sd-vault__item ${got ? 'is-got' : ''}`, attrs: { title: u.description } }, [
      h('span', { class: 'sd-vault__icon', text: got ? (u.kind === 'camera' ? '◉' : '▤') : '🔒' }),
      h('span', { text: u.name.toUpperCase() }),
    ]);
  }));

  const go = () => ctx.startCareer();
  const el = h('section', { class: 'sd-screen sd-page sd-career' }, [
    screenHeader('CAREER', { kicker: c.champion ? 'WORLD CLUB CUP — CHAMPION DIRECTOR' : `TIER ${c.tierIndex + 1} OF ${CAREER_TIERS.length}`, onBack: () => ctx.back() }),
    ladder,
    h('div', { class: 'sd-career__body' }, [
      h('div', { class: 'sd-career__tier sd-panel' }, [
        h('div', { class: 'sd-career__tiername', text: tier.name.toUpperCase() }),
        h('p', { class: 'sd-career__blurb', text: tier.blurb }),
        h('div', { class: 'sd-field__label', text: c.attempt ? `PROGRESS · ATTEMPT ${c.attempt + 1}` : 'PROGRESS' }),
        h('div', { class: 'sd-cpips' }, pips),
        h('div', { class: 'sd-field__label' }, [`TIER AVERAGE · ${c.results.length ? `${Math.round(avg)} (${letterForRating(avg)})` : '—'}`]),
        avgBar,
        h('div', { class: 'sd-field__hint', text: `Average a B (${PROMOTION_RATING}+) across the tier to be promoted. Fall short and you replay the tier with new fixtures.` }),
        rewards.length ? h('div', { class: 'sd-field__label', text: 'PROMOTION REWARDS' }) : null,
        h('div', { class: 'sd-rewards' }, rewards),
      ]),
      h('div', { class: 'sd-career__next' }, [
        h('div', { class: 'sd-section-head' }, [h('span', { text: `NEXT · ${plan.competition.toUpperCase()}` })]),
        fixtureCard(cfg, { big: true }),
        h('div', { class: 'sd-daily__rules' }, [
          h('span', { class: 'sd-chip', text: plan.difficulty.toUpperCase() }),
          h('span', { class: 'sd-chip', text: `${plan.halfLengthSeconds / 60} MIN HALVES` }),
          h('span', { class: 'sd-chip sd-chip--amber', text: `${formatCompact(plan.baseAudience)} WATCHING` }),
        ]),
        matchBrief(cfg.home, cfg.away),
        h('div', { class: 'sd-field__label', text: 'YOUR KIT' }),
        vault,
        h('div', { class: 'sd-quick__spacer' }),
        h('button', { class: 'sd-btn sd-btn--primary sd-btn--golive', attrs: { type: 'button' }, on: { click: go } }, [h('span', { class: 'sd-golive__dot' }), 'GO LIVE', h('kbd', { text: 'ENTER' })]),
      ]),
    ]),
  ]);
  return {
    el,
    mounted() { (el.querySelector('.sd-btn--golive') as HTMLElement | null)?.focus({ preventScroll: true }); },
  };
}
